/**
 * AuditLog — append-only history of Team & Access actions for this org.
 * Read-only view of the last 200 events from psi_access_audit.
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const ACTION_LABELS = {
  team_invite_internal:           'Invited teammate',
  team_invite_external:           'Invited observer / executive',
  team_access_update:             'Updated access',
  team_grant_extended:            'Extended grant',
  team_grant_revoked:             'Revoked grant',
  team_grant_expired:             'Auto-expired grant',
  team_grant_expiry_warning_sent: 'Expiry warning sent',
  team_offboard:                  'Offboarded user',
  team_access_review_kept:        'Access review — kept',
  team_access_review_revoked:     'Access review — revoked',
  team_access_review_closed:      'Access review — closed',
};

function timeAgo(d) {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return min + 'm ago';
  const h = Math.floor(min / 60);
  if (h < 24) return h + 'h ago';
  const days = Math.floor(h / 24);
  if (days < 7) return days + 'd ago';
  return new Date(d).toISOString().slice(0, 10);
}

export default function AuditLog() {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/team/audit`, { credentials: 'include' });
        const j = await r.json();
        if (!cancel) {
          if (j.success) setEvents(j.events || []);
          else setError(j.message || 'Could not load.');
        }
      } catch { if (!cancel) setError('Connection error.'); }
    })();
    return () => { cancel = true; };
  }, []);

  if (error) return <div style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>;
  if (!events) return <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</div>;
  if (events.length === 0) return (
    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 24, color: '#94a3b8', fontSize: 13 }}>
      No team activity yet.
    </div>
  );

  return (
    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '4px 20px' }}>
      {events.map(e => (
        <div key={e.id} style={{ padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <div style={{ fontSize: 13, color: '#0f172a' }}>
              <span style={{ fontWeight: 600 }}>{ACTION_LABELS[e.action] || e.action}</span>
              {e.user_email && (
                <>
                  <span style={{ color: '#cbd5e1', margin: '0 8px' }}>·</span>
                  <span style={{ color: '#475569' }}>{e.user_email}</span>
                </>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>
              {timeAgo(e.created_at)}
            </div>
          </div>
          {e.metadata && Object.keys(e.metadata).length > 0 && (
            <pre style={{
              margin: '6px 0 0 0', fontSize: 11, color: '#475569',
              background: '#fafbfc', padding: '6px 10px', borderRadius: 6,
              fontFamily: 'ui-monospace, monospace', overflow: 'auto',
              maxHeight: 110,
            }}>
              {JSON.stringify(e.metadata, null, 2)}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}
